
import Vue, { PropType } from 'vue';
import { FeedbackReport } from '@run-diffusion/shared';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { AdminUser, adminChoices, FeedbackAdmin, knownNames, loadAdmins } from './feedbackAdmins';

// An admin in the picker, greyed out when they already hold the report themself
type AdminItem = FeedbackAdmin & { disabled: boolean };

/**
 * "Assign" on a feedback report: picks a system admin to work on it, so a lead can hand work out.
 * It lists the admins (read once a page visit, ./feedbackAdmins.ts), you first, searchable by name
 * or email, and emits the one chosen; the report's page claims it for them. Choosing yourself is
 * claiming it.
 */
export default Vue.extend({
	name: 'FeedbackAssignDialog',
	props: {
		open: { type: Boolean, default: false },
		report: { type: Object as PropType<FeedbackReport>, required: true },
		// the loaded reports: their claims name the people who made them
		reports: { type: Array as PropType<FeedbackReport[]>, default: () => [] },
		// the admin using the page, as they sign their work
		me: { type: Object as PropType<{ uid: string, name: string }>, required: true },
		// the person holding it now (not an agent), who can't be chosen again; null for nobody
		holderUid: { type: String as PropType<string | null>, default: null },
		saving: { type: Boolean, default: false },
	},
	data () {
		return {
			users: [] as AdminUser[],
			loading: false,
			failed: false,
			chosen: null as string | null,
		};
	},
	computed: {
		items (): AdminItem[] {
			return adminChoices(this.users, this.me, knownNames(this.reports))
				.map((admin: FeedbackAdmin) => ({ ...admin, disabled: admin.uid === this.holderUid }));
		},
		choice (): AdminItem | null {
			const found: AdminItem | undefined = this.items.find((item: AdminItem) => item.uid === this.chosen);
			return found && !found.disabled ? found : null;
		},
		excerpt (): string {
			const text: string = this.report.happened || '';
			return `#${this.report.number}: ${text.length > 60 ? text.slice(0, 60).trim() + '...' : text}`;
		},
	},
	watch: {
		open: {
			immediate: true,
			handler (open: boolean) {
				if (!open) return;
				this.chosen = null;
				this.load();
				// after the dialog has opened and taken focus itself
				window.setTimeout(() => {
					if (!this.open) return;
					const picker = this.$refs.picker as Vue | undefined;
					const input: HTMLElement | null = picker ? picker.$el.querySelector('input') : null;
					if (input) input.focus();
				}, 250);
			},
		},
	},
	methods: {
		async load () {
			this.loading = true;
			this.failed = false;
			try {
				this.users = await loadAdmins();
			} catch (e) {
				console.error('[feedback] the admins failed to load', e);
				this.failed = true;
			} finally {
				this.loading = false;
			}
		},
		// by name or email, anywhere in either
		matches (item: AdminItem, query: string): boolean {
			const term: string = (query || '').trim().toLowerCase();
			return !term || item.name.toLowerCase().includes(term) || item.email.toLowerCase().includes(term);
		},
		onInput (open: boolean) {
			if (!open) this.cancel();
		},
		cancel () {
			if (!this.saving) this.$emit('cancel');
		},
		confirm () {
			if (!this.choice || this.saving) return;
			const admin: FeedbackAdmin = { uid: this.choice.uid, name: this.choice.name, email: this.choice.email, isMe: this.choice.isMe };
			this.$emit('confirm', admin);
		},
	},
	components: {
		ActionsIsland,
		DialogContent,
		DialogOrBottomSheet,
	},
});
